/**
 * Stop-level editor operations (add / remove / move / edit details).
 *
 * Every function here builds a `QueuedOp` and hands it to the serial mutation
 * queue's `enqueue` (plan D8) — same `apply` optimistic transforms, same
 * `dependsOn` scopes, same tRPC calls as when this lived inside
 * `ItineraryEditor`; only the file changed (#314).
 *
 * The editor keeps ownership of UI state: it decides when to close a picker or
 * a modal, and `saveStopDetails` reports back whether the draft was valid so
 * the stop-details modal can stay open on an unparseable duration.
 */

import { trpc } from '../../lib/trpc';
import { nextOpId, reindex } from './itineraryOptimistic';
import type { QueuedOp } from './useItineraryMutationQueue';
import type { PickedPlace } from './PlacePickerModal';
import type { StopDraft } from './StopDetailsModal';

interface UseItineraryStopOpsArgs {
  itineraryId: string;
  enqueue: (op: QueuedOp) => void;
}

export interface ItineraryStopOps {
  addStopVenue: (dayId: string, venue: { id: string; name: string }) => void;
  addStopPlace: (dayId: string, place: PickedPlace) => void;
  removeStop: (dayId: string, stopId: string) => void;
  moveStop: (
    stopId: string,
    fromDayId: string,
    toDayId: string,
    toOrdinal: number
  ) => void;
  /** Returns false (without enqueuing) when the duration draft is invalid. */
  saveStopDetails: (draft: StopDraft) => boolean;
}

export const useItineraryStopOps = ({
  itineraryId,
  enqueue,
}: UseItineraryStopOpsArgs): ItineraryStopOps => {
  // Raw tRPC mutations (the queue serializes invocation; we call mutateAsync).
  const addStop = trpc.itinerary.addStop.useMutation();
  const removeStopMutation = trpc.itinerary.removeStop.useMutation();
  const reorderStop = trpc.itinerary.reorderStop.useMutation();
  const updateStop = trpc.itinerary.updateStop.useMutation();

  const addStopVenue = (dayId: string, venue: { id: string; name: string }) => {
    const tmpId = `tmp-${nextOpId()}`;
    const op: QueuedOp = {
      id: nextOpId(),
      label: 'add stop',
      dependsOn: { dayId },
      apply: (snap) =>
        reindex({
          ...snap,
          days: snap.days.map((d) =>
            d._id === dayId
              ? {
                  ...d,
                  stops: [
                    ...d.stops,
                    {
                      _id: tmpId,
                      ordinal: d.stops.length,
                      refType: 'venue' as const,
                      venueId: venue.id,
                      place: null,
                      notes: undefined,
                      arrivalTime: undefined,
                      travelToHere: null,
                      isDangling: false,
                    },
                  ],
                }
              : d
          ),
        }),
      run: () =>
        addStop.mutateAsync({
          itineraryId,
          dayId,
          refType: 'venue',
          venueId: venue.id,
        }),
    };
    enqueue(op);
  };

  const addStopPlace = (dayId: string, place: PickedPlace) => {
    const tmpId = `tmp-${nextOpId()}`;
    const op: QueuedOp = {
      id: nextOpId(),
      label: 'add stop',
      dependsOn: { dayId },
      apply: (snap) =>
        reindex({
          ...snap,
          days: snap.days.map((d) =>
            d._id === dayId
              ? {
                  ...d,
                  stops: [
                    ...d.stops,
                    {
                      _id: tmpId,
                      ordinal: d.stops.length,
                      refType: 'place' as const,
                      venueId: null,
                      place,
                      notes: undefined,
                      arrivalTime: undefined,
                      travelToHere: null,
                      isDangling: false,
                    },
                  ],
                }
              : d
          ),
        }),
      run: () =>
        addStop.mutateAsync({
          itineraryId,
          dayId,
          refType: 'place',
          place,
        }),
    };
    enqueue(op);
  };

  const removeStop = (dayId: string, stopId: string) => {
    enqueue({
      id: nextOpId(),
      label: 'remove stop',
      dependsOn: { stopId },
      apply: (snap) =>
        reindex({
          ...snap,
          days: snap.days.map((d) =>
            d._id === dayId
              ? { ...d, stops: d.stops.filter((s) => s._id !== stopId) }
              : d
          ),
        }),
      run: () => removeStopMutation.mutateAsync({ itineraryId, dayId, stopId }),
    });
  };

  const moveStop = (
    stopId: string,
    fromDayId: string,
    toDayId: string,
    toOrdinal: number
  ) => {
    enqueue({
      id: nextOpId(),
      label: 'move stop',
      dependsOn: { stopId },
      apply: (snap) => {
        let moved: (typeof snap.days)[number]['stops'][number] | undefined;
        const stripped = snap.days.map((d) => {
          const found = d.stops.find((s) => s._id === stopId);
          if (found) moved = found;
          return { ...d, stops: d.stops.filter((s) => s._id !== stopId) };
        });
        if (!moved) return snap;
        const movedStop = moved;
        return reindex({
          ...snap,
          days: stripped.map((d) => {
            if (d._id !== toDayId) return d;
            const stops = [...d.stops];
            stops.splice(Math.min(toOrdinal, stops.length), 0, movedStop);
            return { ...d, stops };
          }),
        });
      },
      run: () =>
        reorderStop.mutateAsync({ itineraryId, stopId, toDayId, toOrdinal }),
    });
  };

  const saveStopDetails = (draft: StopDraft): boolean => {
    const { dayId, stopId, notes, arrivalTime, durationMinutes } = draft;
    const trimmedDuration = durationMinutes.trim();
    // Empty string → clear (null over the wire). Anything else must parse as a
    // non-negative integer ≤ 24h; an invalid string aborts the save without a
    // mutation so the modal stays open and the user can fix it.
    let parsedDuration: number | null | undefined;
    if (trimmedDuration === '') {
      parsedDuration = null;
    } else {
      const n = Number(trimmedDuration);
      if (!Number.isFinite(n) || n < 0 || n > 24 * 60 || !Number.isInteger(n)) {
        return false;
      }
      parsedDuration = n;
    }
    enqueue({
      id: nextOpId(),
      label: 'edit stop details',
      dependsOn: { stopId },
      apply: (snap) => ({
        ...snap,
        days: snap.days.map((d) =>
          d._id === dayId
            ? {
                ...d,
                stops: d.stops.map((s) =>
                  s._id === stopId
                    ? {
                        ...s,
                        notes: notes || undefined,
                        arrivalTime: arrivalTime || undefined,
                        durationMinutes:
                          parsedDuration === null
                            ? undefined
                            : (parsedDuration ?? s.durationMinutes),
                      }
                    : s
                ),
              }
            : d
        ),
      }),
      run: () =>
        updateStop.mutateAsync({
          itineraryId,
          dayId,
          stopId,
          notes,
          arrivalTime,
          durationMinutes: parsedDuration,
        }),
    });
    return true;
  };

  return {
    addStopVenue,
    addStopPlace,
    removeStop,
    moveStop,
    saveStopDetails,
  };
};
